import CryptoJS from 'crypto-js';

import { TrackNode } from '../core/track/FJTrack';
import FJMathUtil from './FJMathUtil';
import FJCanvasPool from '../core/FJCanvasPool';
import FJLocalStore from '../store/FJLocalStore';
import FJConfig from '../config/FJConfig';
import FJSubtitleStyleList from '../config/FJSubtitleStyleList';
import FJLoadLocalforageStore from '../store/FJLoadLocalforageStore.js';
import FJVideoUtil from './FJVideoUtil.js';
import FJCoreConfig from '@src/core/FJCoreConfig.js';
let _offscreenCanvas = null;
let _offscreenCtx = null;
let _AudioContext = null;
let _browser = null;
let _adsorptionPauseX = 0;
let _adsorptionPauseY = 0;
let _adsorptionLastNode = null;

/** URL → Blob，仅缓存 HTTP 成功的响应 */
const _getBlobByUrlCache = new Map();
/** 同 URL 并发只发起一次 fetch */
const _getBlobByUrlInflight = new Map();

/** 字幕样式 id → fetchResource 解析后的 JSON（成功才写入） */
const _getSubtitleStyleJsonCache = new Map();
/** 同 id 并发合并为一次请求 */
const _getSubtitleStyleJsonInflight = new Map();

/** 已知扩展名（小写）；`stripKnownExtension` 仅剥离此列表中的后缀，保留文件名内的 `.` */
const KNOWN_FILE_EXTENSIONS = new Set([
    'mp4',
    'mov',
    'webm',
    'm4v',
    'avi',
    'flv',
    'mkv',
    'wmv',
    'mp3',
    'wav',
    'm4a',
    'aac',
    'ogg',
    'flac',
    'jpg',
    'jpeg',
    'png',
    'gif',
    'webp',
    'bmp',
]);

/**
 * 从路径或裸文件名取最后一段。
 * 裸文件名/标题中的 `/`（如 `a/b`、`2026/5/18`）保留；仅对 URL、`\` 路径、绝对路径做分段。
 * @param {string} path
 */
function basenameFromPath(path) {
    if (!path) return '';

    const stripQuery = s => {
        const q = s.search(/[?#]/);
        return q !== -1 ? s.slice(0, q) : s;
    };

    if (/^https?:\/\//i.test(path)) {
        try {
            const url = new URL(path);
            const seg = url.pathname.split('/').filter(Boolean).pop();
            return seg ? stripQuery(decodeURIComponent(seg)) : '';
        } catch {
            const withoutQuery = stripQuery(path);
            const seg = withoutQuery.split('/').filter(Boolean).pop();
            return seg ?? withoutQuery;
        }
    }

    if (path.includes('\\')) {
        const sep = path.lastIndexOf('\\');
        return stripQuery(path.slice(sep + 1));
    }

    if (path.startsWith('/') || /^[a-zA-Z]:[/\\]/.test(path)) {
        const withoutQuery = stripQuery(path);
        const seg = withoutQuery.split('/').filter(Boolean).pop();
        return seg ?? withoutQuery;
    }

    return stripQuery(path);
}

/** @param {string} name */
function stripKnownExtension(name) {
    const m = name.match(/\.([^.]+)$/);
    if (!m) return name;
    const ext = m[1].toLowerCase();
    if (!KNOWN_FILE_EXTENSIONS.has(ext)) return name;
    return name.slice(0, -(ext.length + 1));
}

/** 常见视频帧率（含 NTSC 23.976 / 29.97 / 59.94） */
const COMMON_VIDEO_FPS = [23.976, 24, 25, 29.97, 30, 50, 59.94, 60];

/** 推断值与候选帧率相对误差超过该比例时，回退 defaultFps */
const COMMON_VIDEO_FPS_SNAP_MAX_REL_ERR = 0.035;

const STABLE_FRAGMENT_TIME_EPS = 1e-9;

/**
 * @param {number} rawFps
 * @param {number} defaultFps
 * @returns {number}
 */
function snapToCommonVideoFps(rawFps, defaultFps) {
    if (!Number.isFinite(rawFps) || rawFps <= 0) {
        return defaultFps;
    }
    let best = defaultFps;
    let bestRelErr = Infinity;
    for (const candidate of COMMON_VIDEO_FPS) {
        const relErr = Math.abs(rawFps - candidate) / candidate;
        if (relErr < bestRelErr) {
            bestRelErr = relErr;
            best = candidate;
        }
    }
    return bestRelErr <= COMMON_VIDEO_FPS_SNAP_MAX_REL_ERR ? best : defaultFps;
}

/**
 * @param {{ start_frame?: unknown, end_frame?: unknown, start_time?: unknown, end_time?: unknown }} fragment
 * @returns {number}
 */
function rawFpsFromStableFragment(fragment) {
    const sf = Number(fragment?.start_frame);
    const ef = Number(fragment?.end_frame);
    const st = Number(fragment?.start_time);
    const en = Number(fragment?.end_time);

    const frameSpan = ef - sf;
    const timeSpan = en - st;
    if (
        Number.isFinite(frameSpan) &&
        Number.isFinite(timeSpan) &&
        timeSpan > STABLE_FRAGMENT_TIME_EPS &&
        frameSpan > 0
    ) {
        return frameSpan / timeSpan;
    }

    if (Number.isFinite(ef) && Number.isFinite(en) && en > STABLE_FRAGMENT_TIME_EPS && ef > 0) {
        return ef / en;
    }

    return NaN;
}

const FJUtil = {
    getLanguage: function (tag, callback) {
        fetch(FJConfig.LANGUAGE_PATH + tag + '.json?v=' + FJConfig.VERSION)
            .then(res => res.json())
            .then(json => {
                callback(json);
            })
            .catch(err => {
                console.log(err);
            });
    },

    getPathSuffix: function (path) {
        return path.substr(path.lastIndexOf('.') + 1).trim();
    },
    isLocalhost: function () {
        return Boolean(
            window.location.hostname === 'localhost' ||
            // [::1] is the IPv6 localhost address.
            window.location.hostname === '[::1]' ||
            // 127.0.0.1/8 is considered localhost for IPv4.
            window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/) ||
            window.location.hostname.match(/^192.168(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){2}$/),
        );
    },
    isSupportVideoFrame: function () {
        return typeof VideoFrame !== 'undefined';
    },
    isSupportImageBitmap: function () {
        return typeof ImageBitmap !== 'undefined' && typeof createImageBitmap !== 'undefined';
    },
    isCanvasMatchFrameType(canvas, frameType) {
        if (frameType === 'IMAGEBITMAP') {
            return !!canvas.getContext('bitmaprenderer');
        }
        return !!canvas.getContext('2d');
    },
    getCtxTypeWithFrameType(frameType) {
        if (frameType === 'IMAGEBITMAP') {
            return 'bitmaprenderer';
        }
        return '2d';
    },
    /**
     * 比较两个时间是否相等
     * @param {Number} time1 - 第一个时间
     * @param {Number} time2 - 第二个时间
     * @returns {Boolean} - 如果时间相等返回true，否则返回false
     */
    compareTimes: function (time1, time2, precision = 0.01) {
        return Math.abs(time1 - time2) < precision;
    },
    /**
     * 从 stable 片段列表推断帧率，并吸附为 23.976 / 24 / 25 / 29.97 / 30 / 50 / 59.94 / 60 等常用值。
     * @param {Array<{ start_frame?: unknown, end_frame?: unknown, start_time?: unknown, end_time?: unknown }>} raw
     * @param {number} [defaultFps=30]
     * @returns {number}
     */
    getFpsFromStableFragments(raw, defaultFps = 30) {
        if (!Array.isArray(raw) || raw.length === 0) {
            return defaultFps;
        }
        for (const f of raw) {
            const rawFps = rawFpsFromStableFragment(f);
            if (Number.isFinite(rawFps) && rawFps > 0) {
                return snapToCommonVideoFps(rawFps, defaultFps);
            }
        }
        return defaultFps;
    },
    isDev: function () {
        return process.env.NODE_ENV === 'development';
    },
    rotatePointAroundCenter: function (point, center, angle) {
        const cos = Math.cos((angle * Math.PI) / 180);
        const sin = Math.sin((angle * Math.PI) / 180);
        const dx = point.x - center.x;
        const dy = point.y - center.y;

        return {
            x: center.x + dx * cos - dy * sin,
            y: center.y + dx * sin + dy * cos,
        };
    },
    /**
     * @desc resize（只支持未旋转）的吸附逻辑
     * @param oldRect
     * @param newRect
     * @param viewSize
     * @param {[]} otherRects
     * @param {TrackNode} node
     */
    adsorption2: function (oldRect, newRect, viewSize, otherRects = [], node = TrackNode.MOVE) {
        // 横向的page的吸附线的吸附点
        const horizontalAdsorptions = [
            0,
            viewSize.width * 0.1,
            viewSize.width * 0.5,
            viewSize.width * 0.9,
            viewSize.width,
        ];

        // 竖向的page的吸附线的吸附点
        const verticalAdsorptions = [
            0,
            viewSize.height * 0.1,
            viewSize.height * 0.5,
            viewSize.height * 0.9,
            viewSize.height,
        ];

        // 自身对象的横向吸附线的吸附点
        const oldHorizontalSelfValues = [oldRect.x, oldRect.x + oldRect.width / 2, oldRect.x + oldRect.width];
        const newHorizontalSelfValues = [newRect.x, newRect.x + newRect.width / 2, newRect.x + newRect.width];

        // 自身对象的竖向吸附线的吸附点
        const oldVerticalSelfValues = [oldRect.y, oldRect.y + oldRect.height / 2, oldRect.y + oldRect.height];
        const newVerticalSelfValues = [newRect.y, newRect.y + newRect.height / 2, newRect.y + newRect.height];

        // 其他对象的横向吸附线的吸附点
        const otherHorizontalValues = otherRects.reduce(
            (pre, cur) => [...pre, ...[cur.x, cur.x + cur.width / 2, cur.x + cur.width]],
            [],
        );
        const otherVerticalValues = otherRects.reduce(
            (pre, cur) => [...pre, ...[cur.y, cur.y + cur.height / 2, cur.y + cur.height]],
            [],
        );

        const allHorizontalAdsorptionValues = [...horizontalAdsorptions, ...otherHorizontalValues].sort(
            (a, b) => a - b,
        );
        const allVerticalAdsorptionValues = [...verticalAdsorptions, ...otherVerticalValues].sort((a, b) => a - b);

        let minX; //最小的横向吸附需要的x的偏移
        let minY; // 最小的竖向吸附需要的y的偏移
        if (oldRect.width > oldRect.height) {
            minX = FJCoreConfig.adsorptionOffset;
            minY = (FJCoreConfig.adsorptionOffset * oldRect.height) / oldRect.width;
        } else {
            minY = FJCoreConfig.adsorptionOffset;
            minX = FJCoreConfig.adsorptionOffset / (oldRect.height / oldRect.width);
        }

        let adsorptionOffsetX = 0;
        let adsorptionOffsetY = 0;

        for (let i = 0; i < allHorizontalAdsorptionValues.length; i++) {
            let horizontalAdsorptionSuccess = false;

            const value = allHorizontalAdsorptionValues[i];
            for (let j = 0; j < newHorizontalSelfValues.length; j++) {
                const selfValue = newHorizontalSelfValues[j];
                const oldSelfValue = oldHorizontalSelfValues[j];
                if (selfValue !== oldSelfValue) {
                    if (Math.abs(value - selfValue) < minX) {
                        horizontalAdsorptionSuccess = true;
                        adsorptionOffsetX = value - selfValue;
                        break;
                    }
                }
            }
            if (horizontalAdsorptionSuccess) {
                break;
            }
        }

        for (let i = 0; i < allVerticalAdsorptionValues.length; i++) {
            let verticalAdsorptionSuccess = false;
            const value = allVerticalAdsorptionValues[i];
            for (let j = 0; j < newVerticalSelfValues.length; j++) {
                const selfValue = newVerticalSelfValues[j];
                const oldSelfValue = oldVerticalSelfValues[j];
                if (selfValue !== oldSelfValue) {
                    if (Math.abs(value - selfValue) < minY) {
                        verticalAdsorptionSuccess = true;
                        adsorptionOffsetY = value - selfValue;
                        break;
                    }
                }
            }
            if (verticalAdsorptionSuccess) {
                break;
            }
        }

        // 如果两个方向上都有吸附，那么对更“近”的那个进行吸附
        if (adsorptionOffsetX !== 0 && adsorptionOffsetY !== 0) {
            if (Math.abs(adsorptionOffsetX) > Math.abs(adsorptionOffsetY)) {
                adsorptionOffsetX = 0;
                _adsorptionPauseX = 0;
            } else {
                adsorptionOffsetY = 0;
                _adsorptionPauseY = 0;
            }
        }

        // 改变拖动的 node 后，需要重置“阻力”值，防止某些情况下，该值不为0，导致吸附逻辑错误
        if (_adsorptionLastNode !== node) {
            _adsorptionLastNode = node;
            _adsorptionPauseX = 0;
            _adsorptionPauseY = 0;
        }

        if (node === TrackNode.MOVE) {
            return { ...newRect, x: newRect.x + adsorptionOffsetX, y: newRect.y + adsorptionOffsetY };
        } else {
            const ratio = newRect.width / newRect.height;
            const widthDiff = newRect.width - oldRect.width;
            const heightDiff = newRect.height - oldRect.height;

            const __adsorptionRect = (rect, widthOffset, heightOffset) => {
                switch (node) {
                    case TrackNode.LEFT_TOP:
                        return {
                            ...rect,
                            x: rect.x - widthOffset,
                            y: rect.y - heightOffset,
                            width: newRect.width + widthOffset,
                            height: newRect.height + heightOffset,
                        };
                    case TrackNode.LEFT_BOTTOM:
                        return {
                            ...rect,
                            x: rect.x - widthOffset,
                            width: newRect.width + widthOffset,
                            height: newRect.height + heightOffset,
                        };
                    case TrackNode.RIGHT_TOP:
                        return {
                            ...rect,
                            y: rect.y - heightOffset,
                            width: newRect.width + widthOffset,
                            height: newRect.height + heightOffset,
                        };
                    case TrackNode.RIGHT_BOTTOM:
                        return {
                            ...rect,
                            width: newRect.width + widthOffset,
                            height: newRect.height + heightOffset,
                        };
                    case TrackNode.TOP:
                        return {
                            ...rect,
                            y: rect.y - heightOffset,
                            height: newRect.height + heightOffset,
                        };
                    case TrackNode.LEFT:
                        return {
                            ...rect,
                            x: rect.x - widthOffset,
                            width: newRect.width + widthOffset,
                        };
                    case TrackNode.BOTTOM:
                        return {
                            ...rect,
                            height: newRect.height + heightOffset,
                        };
                    case TrackNode.RIGHT:
                        return {
                            ...rect,
                            width: newRect.width + widthOffset,
                        };
                }
                return rect;
            };

            // 为了统一后面的逻辑，这里针对不同情况将值统一
            switch (node) {
                case TrackNode.LEFT_BOTTOM:
                    adsorptionOffsetX = -adsorptionOffsetX;
                    break;
                case TrackNode.LEFT_TOP:
                    adsorptionOffsetX = -adsorptionOffsetX;
                    adsorptionOffsetY = -adsorptionOffsetY;
                    break;
                case TrackNode.RIGHT_TOP:
                    adsorptionOffsetY = -adsorptionOffsetY;
                    break;
                case TrackNode.LEFT:
                    adsorptionOffsetX = -adsorptionOffsetX;
                    break;
                case TrackNode.TOP:
                    adsorptionOffsetY = -adsorptionOffsetY;
                    break;
            }

            // 大于0代表是拖动变大
            if (widthDiff > 0 || heightDiff > 0) {
                // 如果 adsorptionOffsetX 大于 0，代表需要吸附
                if (adsorptionOffsetX > 0) {
                    _adsorptionPauseX = minX;
                    return __adsorptionRect(newRect, adsorptionOffsetX, adsorptionOffsetX / ratio);
                }
                // 已经吸附到某个边之后继续拖动，需要有一定的“阻力”，每次继续拖动减少
                else if (adsorptionOffsetX < 0 && _adsorptionPauseX > 0) {
                    _adsorptionPauseX -= widthDiff;
                    // 减少到为0代表可以继续拖动，直接将之前阻止的值加上去
                    if (_adsorptionPauseX <= 0) {
                        return __adsorptionRect(newRect, minX, minX / ratio);
                    } else {
                        return __adsorptionRect(newRect, adsorptionOffsetX, adsorptionOffsetX / ratio);
                    }
                }

                if (adsorptionOffsetY > 0) {
                    _adsorptionPauseY = minY;
                    return __adsorptionRect(newRect, adsorptionOffsetY * ratio, adsorptionOffsetY);
                } else if (adsorptionOffsetY < 0 && _adsorptionPauseY > 0) {
                    _adsorptionPauseY -= heightDiff;
                    if (_adsorptionPauseY <= 0) {
                        return __adsorptionRect(newRect, minY * ratio, minY);
                    } else {
                        return __adsorptionRect(newRect, adsorptionOffsetY * ratio, adsorptionOffsetY);
                    }
                }

                return newRect;
            } else if (widthDiff < 0 || heightDiff < 0) {
                if (adsorptionOffsetX < 0) {
                    _adsorptionPauseX = minX;
                    return __adsorptionRect(newRect, adsorptionOffsetX, adsorptionOffsetX / ratio);
                } else if (adsorptionOffsetX > 0 && _adsorptionPauseX > 0) {
                    _adsorptionPauseX += widthDiff;
                    if (_adsorptionPauseX <= 0) {
                        return __adsorptionRect(newRect, -minX, -minX / ratio);
                    } else {
                        return __adsorptionRect(newRect, adsorptionOffsetX, adsorptionOffsetX / ratio);
                    }
                }

                if (adsorptionOffsetY < 0) {
                    _adsorptionPauseY = minY;
                    return __adsorptionRect(newRect, adsorptionOffsetY * ratio, adsorptionOffsetY);
                } else if (adsorptionOffsetY > 0 && _adsorptionPauseY > 0) {
                    _adsorptionPauseY += heightDiff;
                    if (_adsorptionPauseY <= 0) {
                        return __adsorptionRect(newRect, -minY * ratio, -minY);
                    } else {
                        return __adsorptionRect(newRect, adsorptionOffsetY * ratio, adsorptionOffsetY);
                    }
                }

                return newRect;
            }

            return newRect;
        }
    },
    addEvent: function (element, event, handler, useCapture = false) {
        if (!element) {
            return;
        }
        if (element.addEventListener) {
            element.addEventListener(event, handler, useCapture);
        } else if (element.attachEvent) {
            element.attachEvent('on' + event, handler);
        }
    } /**
     * @description 更新鼠标样式
     * @param cursor
     * @param element
     */,
    updateCursor: function (cursor, element) {
        // const target = element || FJArchiveUtil.getAuxiliary();
        // 辅助层级调整，不再绑定到辅助层级
        const target = element || document.getElementById('preview-container');
        const browser = this.getBrowser();
        let cursorValue;
        const { default_se_resize, default_nw_resize, default_horizontal_resize, default_vertical_resize } =
            FJCoreConfig;
        switch (cursor) {
            case 'pointer':
                cursorValue = 'u-cursor__pointer';
                break;
            case 'move':
                cursorValue = 'u-cursor__move';
                break;
            case 'default':
                cursorValue = 'u-cursor__default';
                break;
            case 'grab':
                cursorValue = 'u-cursor__grab';
                break;
            case 'grabbing':
                cursorValue = 'u-cursor__grabbing';
                break;
            case 'auto':
                cursorValue = 'u-cursor__auto';
                break;
            case 'all-scroll':
                cursorValue = 'u-cursor__all-scroll';
                break;
            case 'trim':
                cursorValue = 'u-cursor__trim';
                break;
            case default_se_resize:
                if (browser === 'IE' || browser === 'Edge') {
                    cursorValue = 'u-cursor__nwse';
                } else {
                    cursorValue = 'u-cursor__nwse_custom';
                }
                break;
            case default_nw_resize:
                if (browser === 'IE' || browser === 'Edge') {
                    cursorValue = 'u-cursor__nesw';
                } else {
                    cursorValue = 'u-cursor__nesw_custom';
                }
                break;
            case default_vertical_resize:
                if (browser === 'IE' || browser === 'Edge') {
                    cursorValue = 'u-cursor__ns';
                } else {
                    cursorValue = 'u-cursor__ns_custom';
                }
                break;
            case default_horizontal_resize:
                if (browser === 'IE' || browser === 'Edge') {
                    cursorValue = 'u-cursor__ew';
                } else {
                    cursorValue = 'u-cursor__ew_custom';
                }
                break;
            case 'rotate':
                cursorValue = 'u-cursor__rotate';
                break;
            case 'crosshair':
                cursorValue = 'u-cursor__crosshair';
                break;
            default:
                cursorValue = 'u-cursor__auto';
        }

        const allList = [
            'u-cursor__auto',
            'u-cursor__pointer',
            'u-cursor__move',
            'u-cursor__ew',
            'u-cursor__ew_custom',
            'u-cursor__ns',
            'u-cursor__ns_custom',
            'u-cursor__nesw',
            'u-cursor__nesw_custom',
            'u-cursor__nwse',
            'u-cursor__nwse_custom',
            'u-cursor__rotate',
            'u-cursor__crosshair',
        ];
        target.classList.add(cursorValue);
        target.classList.remove(...allList.filter(value => value !== cursorValue));
    },
    isTextStyleEqual(textStyle1, textStyle2) {
        return (
            textStyle1?.fontColor === textStyle2?.fontColor &&
            textStyle1?.uppercaseType === textStyle2?.uppercaseType &&
            textStyle1?.isBold === textStyle2?.isBold &&
            textStyle1?.isItalic === textStyle2?.isItalic
        );
    },
    secondToInstant: function (second, hasHour, hasMs = false) {
        if (!second || second < 0) {
            return hasHour ? '00:00:00' : '00:00';
        }

        let ms = (second * 10) % 10;
        let s = second;
        let m;
        let h;

        let format = value => {
            return value < 10 ? '0' + value : value;
        };

        // 显示小时
        if (hasHour) {
            h = Math.floor(s / 3600);
            s -= h * 3600;
            m = Math.floor(s / 60);
            s -= m * 60;
            h = format(h);
            m = format(m);
            s = format(Math.floor(s));
            if (hasMs) {
                return `${h}:${m}:${s}:${ms}`;
            }
            return `${h}:${m}:${s}`;
        }

        m = Math.floor(second / 60);
        s = s - m * 60;
        m = format(m);
        s = format(Math.floor(s));

        // 显示毫秒
        if (hasMs) {
            return `${m}:${s}.${ms}`;
        }
        return `${m}:${s}`;
    },
    getRichFontFamily(fontFamily, isBold, isItalic) {
        return fontFamily + '' + (isBold ? 'bold' : 'normal') + '' + (isItalic ? 'italic' : 'normal');
    },
    /**
     * @description 判断value是否为undefined
     * @param value
     * @return {boolean}
     */
    isUndefined: function (value) {
        return value === void 0;
    },
    hashValue: function (value, length = 16) {
        return CryptoJS.SHA256(value.toString()).toString().substring(0, length);
    },
    /**
     * @desc 克隆canvas
     * @param canvas
     * @param maxSize
     * @param usePoolCanvas
     * @param contextType
     * @return {null|HTMLCanvasElement}
     */
    cloneCanvas: function (canvas, maxSize, usePoolCanvas, contextType) {
        if (!canvas || canvas.width === 0 || canvas.height === 0) return null;

        let size = { width: canvas.width, height: canvas.height };
        if (maxSize) {
            size = FJMathUtil.adaptSizeWithMaxSize(size, maxSize);
        }
        let newCanvas = usePoolCanvas ? FJCanvasPool.getCanvas(contextType) : document.createElement('canvas');
        newCanvas.width = size.width;
        newCanvas.height = size.height;
        newCanvas.getContext('2d').drawImage(canvas, 0, 0, size.width, size.height);
        return newCanvas;
    },
    getArrayBufferByUrl: function (url) {
        return new Promise(resolve => {
            try {
                fetch(url)
                    .then(async res => {
                        const blob = await res.arrayBuffer();
                        resolve(blob);
                    })
                    .catch(err => {
                        resolve(null);
                    });
            } catch (e) {
                resolve(null);
            }
        });
    },
    convertToArrayBuffer: function (source) {
        return new Promise((resolve, reject) => {
            // source is blob or blob url
            if (source === null) {
                reject(null);
            }
            if (typeof source === 'string') {
                this.getArrayBufferByUrl(source)
                    .then(buffer => {
                        resolve(buffer);
                    })
                    .catch(err => {
                        resolve(null);
                    });
            } else {
                try {
                    const fileReader = new FileReader();
                    fileReader.onload = () => {
                        resolve(fileReader.result);
                    };
                    fileReader.onerror = () => {
                        resolve(null);
                    };
                    fileReader.readAsArrayBuffer(source);
                } catch (e) {
                    console.log(e, source);
                    resolve(null);
                }
            }
        });
    },
    /**
     * @description 获取资源的唯一 id
     * @param {Blob | File} target
     * @param {boolean} legancy - 可选参数。是否使用旧版的生成规则
     * @returns
     */
    hashEncode: async function (target) {
        let threshold = 1048576; // 1M
        let source = target;
        const size = source.size || source.length;

        if (size > threshold) {
            // 新版本的生成基于 gapLength 进行分割
            const gapLength = 10;
            const type = source.type;
            const originSliceLength = Math.floor(size / gapLength);
            const finalSliceLength = Math.floor(threshold / gapLength);
            const sliceBlobs = [];
            for (let i = 0; i < gapLength; i++) {
                const start = originSliceLength * i;
                const end = start + finalSliceLength;
                sliceBlobs.push(source.slice(start, end));
            }
            source = new Blob(sliceBlobs, { type });
        }

        let arrayBuffer;
        if (source.arrayBuffer) {
            arrayBuffer = await source.arrayBuffer();
        } else {
            arrayBuffer = await this.convertToArrayBuffer(source);
        }
        const binaryString = new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '');
        const hash = CryptoJS.SHA256(binaryString).toString();
        return hash;
    },
    removeEvent: function (element, event, handler, useCapture = false) {
        if (!element) {
            return;
        }
        if (element.removeEventListener) {
            element.removeEventListener(event, handler, useCapture);
        } else if (element.detachEvent) {
            element.detachEvent('on' + event, handler);
        }
    },

    /**
     * @desc 深拷贝
     * @param {any} obj
     * @returns {any}
     */
    deepClone: function (obj) {
        if (typeof obj !== 'object' || obj === null) {
            return obj;
        }

        let newObj;
        if (obj instanceof Array) {
            newObj = [];
            for (let i = 0; i < obj.length; i++) {
                newObj[i] = this.deepClone(obj[i]);
            }
        } else if (obj instanceof ImageData) {
            newObj = new ImageData(new Uint8ClampedArray(obj.data), obj.width, obj.height);
        } else {
            newObj = {};
            for (let key in obj) {
                if (Object.prototype.hasOwnProperty.call(obj, key)) {
                    newObj[key] = this.deepClone(obj[key]);
                }
            }
        }

        return newObj;
    },
    /**
     * @description 轮询
     * @param checker - 检查函数
     * @param interval - 轮询间隔时间（ms）
     * @param timeout - 最大轮询时间（ms）
     * @param throwErrorTimeout - 是否在在 timeout 时抛出错误
     * @return {Promise<void>}
     */
    polling: async function (checker, interval = 1, timeout = 2000, throwErrorTimeout, signal) {
        // 检查 signal 是否已中断
        if (signal?.aborted) return;

        const oldTime = performance.now();
        const isTimeout = () => {
            const out = performance.now() - oldTime >= timeout;
            if (throwErrorTimeout && out) {
                throw new Error('POLLING_TIMEOUT');
            }
            return out;
        };

        while (!checker() && !isTimeout()) {
            // 每次轮询前检查是否中断
            if (signal?.aborted) return;
            await this.wait(interval / 1000);
        }
    },
    getQueryString: function (name) {
        let reg = new RegExp('(^|&)' + name + '=([^&]*)(&|$)', 'i');
        let r = window.location.search.substr(1).match(reg);
        if (r != null) return unescape(r[2]);
        return null;
    },
    getCurrentUrl: function () {
        let lange = FJLocalStore.currentTag();
        let url = '';
        if (lange === 'en' || lange === 'it') {
            url = '/';
        } else {
            url = '/' + lange + '/';
        }
        return url;
    },
    /**
     * @description 改变地址栏链接
     * @param {*} url
     */
    changeLocation: function (url) {
        window.history.replaceState({ ...window.history.state, id: 0 }, document.title, url);
    },
    /**
     * 获取文件名（不含路径）；不修改后缀。标题中含 `/` 时保留全文。
     * @param {string} path 路径、文件名、标题或带 query 的 URL
     * @returns {string}
     */
    getFileName: function (path) {
        return basenameFromPath(path);
    },
    /**
     * 获取不含路径的文件主名：先取 basename，再仅去掉已知媒体扩展名（见模块内 KNOWN_FILE_EXTENSIONS）。
     * 例如 `dir/1.08min.mp4` → `1.08min`，`report.2024` → `report.2024`。
     * @param {string} path
     * @returns {string}
     */
    getBaseName: function (path) {
        return stripKnownExtension(basenameFromPath(path));
    },
    /**
     * @desc 获取音频上下文
     * @returns {AudioContext}
     */
    getAudioContext: function () {
        if (_AudioContext) {
            return _AudioContext;
        } else {
            // @ts-ignore
            _AudioContext = new (window.AudioContext || window.webkitAudioContext)();
            return _AudioContext;
        }
    },
    getAudioSampleRate: function () {
        let sr = this.getAudioContext().sampleRate;
        return sr <= 44100 ? sr : 48000;
    },

    couldOptFrame() {
        return typeof createImageBitmap !== 'undefined' && typeof OffscreenCanvas !== 'undefined';
    },
    /**
     * @desc 获取当前浏览器
     * @return {string}
     */
    getBrowser: function () {
        if (_browser) {
            return _browser;
        }
        const userAgentDataBrowserData = { 'Google Chrome': 'Chrome', Opera: 'Opera', 'Microsoft Edge': 'Edge' };
        // 使用新的方式获取
        let __getBrowserUseNewMethods = () => {
            let browser = '';
            if (navigator && navigator.userAgentData) {
                const userAgentData = navigator.userAgentData;
                const isMobile = userAgentData.mobile ? '.Mobile' : '';
                const brands = userAgentData.brands;
                let keys = Object.keys(userAgentDataBrowserData);
                for (let i = 0; i < brands.length; i++) {
                    const brand = brands[i].brand;
                    let index = keys.indexOf(brand);
                    if (index !== -1) {
                        let key = keys[index];
                        browser = userAgentDataBrowserData[key] + isMobile;
                        break;
                    }
                }
            }
            return browser;
        };
        // 使用老的方式获取
        let __getBrowserUseOldMethods = () => {
            let Sys = {};
            let ua = navigator.userAgent.toLowerCase();
            let s;
            let isMobile = '';

            // eslint-disable-next-line no-unused-expressions, no-cond-assign
            (s = ua.match(/rv:([\d.]+)\) like gecko/))
                ? (Sys.ie = s[1])
                : // eslint-disable-next-line no-cond-assign
                  (s = ua.match(/msie ([\d.]+)/))
                  ? (Sys.ie = s[1])
                  : // eslint-disable-next-line no-cond-assign
                    (s = ua.match(/edge\/([\d.]+)/))
                    ? (Sys.edge = s[1])
                    : // eslint-disable-next-line no-cond-assign
                      (s = ua.match(/edg\/([\d.]+)/))
                      ? (Sys.edge = s[1])
                      : // eslint-disable-next-line no-cond-assign
                        (s = ua.match(/firefox\/([\d.]+)/))
                        ? (Sys.firefox = s[1])
                        : // eslint-disable-next-line no-cond-assign
                          (s = ua.match(/opera.([\d.]+)/))
                          ? (Sys.opera = s[1])
                          : // eslint-disable-next-line no-cond-assign
                            (s = ua.match(/opr\/([\d.]+)/))
                            ? (Sys.opera = s[1])
                            : // eslint-disable-next-line no-cond-assign
                              (s = ua.match(/chrome\/([\d.]+)/))
                              ? (Sys.chrome = s[1])
                              : // eslint-disable-next-line no-cond-assign
                                (s = ua.match(/version\/([\d.]+).*safari/))
                                ? (Sys.safari = s[1])
                                : 0;

            if (ua.match(/mobile/)) isMobile = '.Mobile';

            if (Sys.ie) return 'IE' + isMobile;
            else if (Sys.edge) return 'Edge' + isMobile;
            else if (Sys.firefox) return 'FF' + isMobile;
            else if (Sys.opera) return 'Opera' + isMobile;
            else if (Sys.safari) return 'Safari' + isMobile;
            else if (Sys.chrome) return 'Chrome' + isMobile;
            else return '';
        };

        let browser = __getBrowserUseNewMethods();
        if (!browser) browser = __getBrowserUseOldMethods();
        _browser = browser;
        return browser;
    },
    getChromeVersion() {
        const arr = navigator.userAgent.split(' ');
        let chromeVersion = '';
        for (let i = 0; i < arr.length; i++) {
            if (/chrome/i.test(arr[i])) chromeVersion = arr[i];
        }
        if (chromeVersion) {
            return Number(chromeVersion.split('/')[1].split('.')[0]);
        } else {
            return 0;
        }
    },
    getImageBitmap(canvas) {
        const width = canvas.width;
        const height = canvas.height;
        _offscreenCanvas = _offscreenCanvas || new OffscreenCanvas(width, height);

        if (_offscreenCanvas.width !== width) {
            _offscreenCanvas.width = width;
        }
        if (_offscreenCanvas.height !== height) {
            _offscreenCanvas.height = height;
        }
        _offscreenCtx = _offscreenCtx || _offscreenCanvas.getContext('2d');
        if (_offscreenCtx) {
            _offscreenCtx.drawImage(canvas, 0, 0, width, height);
            return _offscreenCanvas.transferToImageBitmap();
        }
        return null;
    },
    getUseFileName: function (name, suffix) {
        const now = new Date();
        // 获取年、月、日、时、分、秒
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0'); // 月份从0开始，需要加1并补零
        const day = String(now.getDate()).padStart(2, '0'); // 补零
        const hours = String(now.getHours()).padStart(2, '0'); // 补零
        const minutes = String(now.getMinutes()).padStart(2, '0'); // 补零
        const seconds = String(now.getSeconds()).padStart(2, '0'); // 补零

        const px = name ? name + '-' : '';
        // 格式化日期和时间为 2024-07-17T111525
        return `${px}Made with FlexClip AI-${year}-${month}-${day}T${hours}${minutes}${seconds}${suffix}`;
    },
    wait: function (seconds = 0.3) {
        return new Promise(resolve => {
            const timer = setTimeout(() => {
                clearTimeout(timer);
                resolve();
            }, seconds * 1000);
        });
    },
    /**
     * 判断比例 key（如 '9:16'、'16:9'）与素材宽/高是否同一显示宽高比（允许极小浮点误差）。
     * @param {string | undefined} aspectRatioKey
     * @param {number | undefined} width
     * @param {number | undefined} height
     * @returns {boolean}
     */
    aspectRatioKeyMatchesFileData: function (aspectRatioKey, width, height) {
        if (aspectRatioKey == null || aspectRatioKey === '' || !width || !height || width <= 0 || height <= 0) {
            return false;
        }
        const parts = String(aspectRatioKey)
            .trim()
            .split(':')
            .map(s => Number(String(s).trim()));
        if (parts.length < 2 || !parts.every(n => Number.isFinite(n) && n > 0)) {
            return false;
        }
        const [rw, rh] = parts;
        const rFile = width / height;
        const rKey = rw / rh;
        const eps = 1e-5 * Math.max(rFile, rKey, 1);
        return Math.abs(rFile - rKey) <= eps;
    },
    /**
     * 素材为 3:4 而导出选 4:5，或素材为 4:5 而导出选 3:4（互相输出）时返回 true，用于关闭人脸链路。
     * @param {string | undefined} aspectRatioKey 导出比例 key，如 '3:4'、'4:5'
     * @param {number | undefined} width
     * @param {number | undefined} height
     * @returns {boolean}
     */
    aspectRatioKeyIs34vs45CrossFromFile: function (aspectRatioKey, width, height) {
        if (aspectRatioKey == null || aspectRatioKey === '' || !width || !height || width <= 0 || height <= 0) {
            return false;
        }
        const key = String(aspectRatioKey).trim();
        if (key !== '3:4' && key !== '4:5') {
            return false;
        }
        const rFile = width / height;
        const r34 = 3 / 4;
        const r45 = 4 / 5;
        const eps34 = 1e-5 * Math.max(rFile, r34, 1);
        const eps45 = 1e-5 * Math.max(rFile, r45, 1);
        const fileIs34 = Math.abs(rFile - r34) <= eps34;
        const fileIs45 = Math.abs(rFile - r45) <= eps45;
        return (fileIs34 && key === '4:5') || (fileIs45 && key === '3:4');
    },
    /**
     * 按 URL 取 Blob：优先读本地（与 `FJVideoUtil.saveFileListToLocalStorage([{ key: url, file }])` 同一 key），否则 fetch。
     * @param {string} url
     * @returns {Promise<Blob | null>}
     */
    getBlobByUrl: function (url, saveLocal = false) {
        if (url == null || url === '') {
            return Promise.resolve(null);
        }
        const cached = _getBlobByUrlCache.get(url);
        if (cached !== undefined) {
            return Promise.resolve(cached);
        }
        const pending = _getBlobByUrlInflight.get(url);
        if (pending) {
            return pending;
        }
        const p = (async () => {
            try {
                const stored = await FJLoadLocalforageStore.getItem(url);
                if (stored && stored.data != null) {
                    const raw = stored.data;
                    if (raw instanceof Blob) {
                        _getBlobByUrlCache.set(url, raw);
                        return raw;
                    }
                }
            } catch {
                // 本地读取失败则走网络
            }
            try {
                const res = await fetch(url);
                const blob = await res.blob();
                if (res.ok && blob) {
                    _getBlobByUrlCache.set(url, blob);
                    if (saveLocal) {
                        FJVideoUtil.saveFileListToLocalStorage([{ key: url, file: blob }]);
                    }
                }
                return blob;
            } catch {
                return null;
            }
        })().finally(() => {
            _getBlobByUrlInflight.delete(url);
        });
        _getBlobByUrlInflight.set(url, p);
        return p;
    },
    /**
     * 按字幕样式 id 拉取样式 JSON（与详情 init 原 __getSubtitleJson 一致），带缓存与并发去重。
     * @param {string | undefined} subtitleStyleId
     * @returns {Promise<object | undefined>}
     */
    getSubtitleStyleJson: function (subtitleStyleId) {
        if (subtitleStyleId == null || subtitleStyleId === '' || subtitleStyleId === 'None') {
            return Promise.resolve(undefined);
        }
        const cached = _getSubtitleStyleJsonCache.get(subtitleStyleId);
        if (cached !== undefined) {
            return Promise.resolve(cached);
        }
        const pending = _getSubtitleStyleJsonInflight.get(subtitleStyleId);
        if (pending) {
            return pending;
        }
        const p = (async () => {
            try {
                const subtitleStyle = FJSubtitleStyleList.find(style => style.id === subtitleStyleId);
                if (!subtitleStyle) {
                    return undefined;
                }
                const { default: FJNetworkManager } = await import('../core/FJNetworkManager');
                const json = await FJNetworkManager.fetchResource(
                    `${FJConfig.RESOURCE_SERVER}${subtitleStyle.jsonUrl}?v=${FJConfig.STATIC_RESOURCE_VERSION}`,
                );
                if (!json) {
                    return undefined;
                }
                _getSubtitleStyleJsonCache.set(subtitleStyleId, json);
                return json;
            } catch {
                return undefined;
            }
        })().finally(() => {
            _getSubtitleStyleJsonInflight.delete(subtitleStyleId);
        });
        _getSubtitleStyleJsonInflight.set(subtitleStyleId, p);
        return p;
    },
    isPanelArea: function (e, object) {
        if (!e) {
            return false;
        }
        let point = {
            x: e.clientX,
            y: e.clientY,
        };

        try {
            // 避免小数计算导致误差
            const offset = 1;
            const clientRect = object.getBoundingClientRect();
            let rect = {
                x: clientRect.left - offset,
                y: clientRect.top - offset,
                width: clientRect.width + offset,
                height: clientRect.height + offset,
            };

            return FJMathUtil.isPointInRect(point, rect);
        } catch (e) {
            return false;
        }
    },
    /**
     * 详情页：秒数格式化为 mm:ss（字幕区间、时间轴展示）
     * @param {number} totalSeconds
     * @returns {string}
     */
    formatDetailsTimeMmSs: function (totalSeconds) {
        const s = Math.max(0, totalSeconds);
        const mm = Math.floor(s / 60);
        const ss = Math.floor(s % 60);
        return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
    },
    /**
     * 字幕块区间展示，如 [00:00-00:24]
     * @param {number} startSec
     * @param {number} endSec
     * @returns {string}
     */
    formatDetailsSubtitleRangeLabel: function (startSec, endSec) {
        return `[${this.formatDetailsTimeMmSs(startSec)}-${this.formatDetailsTimeMmSs(endSec)}]`;
    },
    /**
     * 任务列表：接口时间戳格式化为展示文案，如 `2026-04-15 15:42`（本地时区，秒级/毫秒级均支持）
     * @param {unknown} ts
     * @returns {string}
     */
    formatTaskListTimestamp: function (ts) {
        if (ts == null || ts === '') {
            return '';
        }
        const n = Number(ts);
        if (!Number.isFinite(n)) {
            return String(ts);
        }
        const ms = n < 1e12 ? n * 1000 : n;
        const d = new Date(ms);
        if (Number.isNaN(d.getTime())) {
            return String(ts);
        }
        const y = d.getFullYear();
        const mo = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const h = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        return `${y}-${mo}-${day} ${h}:${min}`;
    },
    /**
     * @description 格式化 ISO 时间戳为 YYYY.M.D HH:mm 格式, 例如 2026.4.24 11:57
     * @param {string} isoString - ISO 8601 格式的时间戳字符串
     * @returns {string}
     */
    formatIsoTimestamp: function (isoString) {
        if (!isoString || typeof isoString !== 'string') {
            return '';
        }

        const d = new Date(isoString);
        if (isNaN(d.getTime())) {
            return ''; // Invalid date string
        }

        const year = d.getFullYear();
        const month = d.getMonth() + 1; // getMonth() is zero-based
        const day = d.getDate();
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');

        return `${year}.${month}.${day} ${hours}:${minutes}`;
    },
    /**
     * 释放单个图片元素的内存（通用场景）
     * @param {HTMLImageElement} imgEl - 原生 img DOM 元素
     */
    releaseImageMemory: function (imgEl) {
        if (!imgEl || imgEl.tagName !== 'IMG') return;

        // 1. 核心：替换为 1x1 空白 Base64 图（体积 < 100 字节，几乎不占用内存）
        const blankBase64 = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
        imgEl.src = blankBase64;

        // 2. （可选）清空 srcset（若使用了响应式图片），避免残留资源引用
        if (imgEl.srcset) {
            imgEl.srcset = '';
        }

        // 3. （可选）清空自定义属性，杜绝引用残留（避免 GC 无法回收）
        Object.keys(imgEl.dataset).forEach(key => {
            delete imgEl.dataset[key];
        });

        // 4. （可选）移除图片上的所有事件监听器（若绑定过），避免闭包引用
        // 注意：需使用具名函数绑定事件，才能正常移除（匿名函数无法移除）
        if (imgEl.onload) imgEl.onload = null;
        if (imgEl.onerror) imgEl.onerror = null;
    },
    forbidEvent: function (e) {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
    },
    getReloadNewUrl: function (url) {
        if (!url) return url; // 处理空字符串或未定义的情况

        // 检查是否已经包含 v 参数
        const hasVParam = url.includes('v=');
        const separator = url.includes('?') ? '&' : '?'; // 判断是否需要添加 ? 或 &

        if (hasVParam) {
            // 如果已经包含 v 参数，则覆盖其值
            return url.replace(/([?&]v=)[^&]*/, `$1${new Date().getTime()}`);
        } else {
            // 如果未包含 v 参数，则追加
            return `${url}${separator}v=${new Date().getTime()}`;
        }
    },
    /**
     * @description 判断是否为 YouTube 视频链接
     * @param {string} url
     * @returns {boolean}
     */
    isYoutubeUrl: function (url) {
        if (!url || typeof url !== 'string') return false;
        return /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)/.test(url);
    },
    extractYoutubeVideoId: function (url) {
        if (!url) return '';

        // 支持多种 YouTube URL 格式
        const patterns = [
            /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([^&\n?#]+)/,
            /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
        ];

        for (const pattern of patterns) {
            const match = url.match(pattern);
            if (match && match[1]) {
                return match[1];
            }
        }

        return '';
    },
    formatTime: function (time, keepMs, mDigit, msDigit, hasAutoHour = false, forceShowHour = false) {
        if (!time && time !== 0) return mDigit === 2 ? '00:00' : '0:00';
        if (typeof time !== 'number') time = Number(time);
        if (isNaN(time)) return mDigit === 2 ? '00:00' : '0:00';

        time = time.toFixed(2);
        let m = Math.floor(time / 60);
        let s = keepMs ? Math.floor(time % 60) : Math.round(time % 60); // 显示毫秒的地方s要用向下取整
        // 小时
        let hour = forceShowHour ? '00' : 0;
        if (s === 60) {
            // 四舍五入可能造成s为60
            m += 1;
            s = 0;
        }
        if (s < 10) s = '0' + s;

        if (hasAutoHour && m >= 60) {
            hour = Math.floor(m / 60);
            m = m % 60;
        }
        if (m < 10 && mDigit === 2) m = '0' + m;

        if (keepMs) {
            time = (Math.round(time * 10) / 10).toFixed(2).toString();
            let ms = time.substr(time.lastIndexOf('.') + 1, msDigit || 2);
            if (hour !== 0) {
                return `${hour}:${m}:${s}.${ms}`;
            }
            return `${m}:${s}.${ms}`;
        } else {
            if (hour !== 0) {
                return `${hour}:${m}:${s}`;
            }
            return `${m}:${s}`;
        }
    },
    parseString(str) {
        try {
            return JSON.parse(str);
        } catch (e) {
            return str;
        }
    },
    copyTextToClipBoard: function (text, tag) {
        if (!tag) {
            tag = document.createElement('input');
            document.body.appendChild(tag);
            window.queueMicrotask(() => {
                tag.remove();
            });
        }
        tag.value = text;
        tag.select();
        document.execCommand('copy');
    },
    /**
     * @description 判断字符串是否包含多个网址
     * @param {string} text
     * @returns {boolean}
     */
    containsMultipleUrls: function (text) {
        if (!text || typeof text !== 'string') {
            return false;
        }
        // 改进后的正则表达式，只查找 URL 的起始部分（http:// 或 https://）
        // 这样即使 URL 之间没有空格也能正确计数
        const urlRegex = /https?:\/\//gi;
        const matches = text.match(urlRegex);
        // 如果匹配到的起始部分数量大于1，则认为包含多个网址
        return matches ? matches.length > 1 : false;
    },
};

export default FJUtil;
